if (!customElements.get('op-metal-chart')) {
  const previousByChart = new Map();
  const RADIUS = 205;
  const STROKE = 90;
  const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

  const easeInOutCubic = (t) => (t < 0.5 ? 4 * t * t * t : 1 - (-2 * t + 2) ** 3 / 2);

  const parseSegments = (value) => {
    try {
      return JSON.parse(value || '[]').map((item) => ({
        name: item.name,
        pct: Number(item.pct) || 0,
        color: item.color,
      }));
    } catch (error) {
      return [];
    }
  };

  const mixKey = (segments) => segments.map((item) => `${item.name}:${item.pct}`).join('|');

  const buildTween = (from, to) => {
    const order = [];
    to.forEach((item) => {
      if (!order.includes(item.name)) order.push(item.name);
    });
    from.forEach((item) => {
      if (!order.includes(item.name)) order.push(item.name);
    });

    return order.map((name) => {
      const start = from.find((item) => item.name === name);
      const end = to.find((item) => item.name === name);
      return {
        name,
        color: (end || start).color,
        from: start ? start.pct : 0,
        to: end ? end.pct : 0,
      };
    });
  };

  customElements.define(
    'op-metal-chart',
    class OpMetalChart extends HTMLElement {
      connectedCallback() {
        this.animateToCurrent();
      }

      disconnectedCallback() {
        if (this.raf) cancelAnimationFrame(this.raf);
      }

      setup() {
        if (this.svg) return;
        this.innerHTML = `
          <svg class="op-know-setting__chart-svg" preserveAspectRatio="xMidYMid meet" viewBox="-20 -20 540 540" height="100%" width="100%">
            <g transform="rotate(-90 250 250)"></g>
          </svg>
        `;
        this.svg = this.querySelector('svg');
        this.group = this.querySelector('g');
      }

      syncCircles(count) {
        const circles = [...this.group.querySelectorAll('circle')];
        while (circles.length < count) {
          const circle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
          circle.setAttribute('cx', '250');
          circle.setAttribute('cy', '250');
          circle.setAttribute('r', String(RADIUS));
          circle.setAttribute('fill', 'none');
          circle.setAttribute('stroke-width', String(STROKE));
          circle.setAttribute('stroke-linecap', 'butt');
          this.group.appendChild(circle);
          circles.push(circle);
        }
        while (circles.length > count) {
          circles.pop().remove();
        }
        return [...this.group.querySelectorAll('circle')];
      }

      paint(segments) {
        this.setup();
        const visible = segments.filter((item) => item.pct > 0.001);
        const circles = this.syncCircles(visible.length);
        let offset = 0;

        visible.forEach((item, index) => {
          const length = (item.pct / 100) * CIRCUMFERENCE;
          const circle = circles[index];
          circle.setAttribute('stroke', item.color);
          circle.setAttribute('stroke-dasharray', `${length} ${CIRCUMFERENCE}`);
          circle.setAttribute('stroke-dashoffset', String(-offset));
          offset += length;
        });
      }

      animateToCurrent() {
        const next = parseSegments(this.getAttribute('data-segments'));
        if (!next.length) {
          this.innerHTML = '';
          this.svg = null;
          this.group = null;
          return;
        }

        const previous = previousByChart.get('metal');
        const from =
          previous && previous.length ? previous : next.map((item) => ({ ...item, pct: 0 }));

        if (this.raf) cancelAnimationFrame(this.raf);

        if (mixKey(from) === mixKey(next) && from.some((item) => item.pct > 0)) {
          this.paint(next);
          previousByChart.set('metal', next);
          return;
        }

        this.paint(from);

        const tween = buildTween(from, next);
        const duration = 900;
        const start = performance.now();

        const tick = (now) => {
          const progress = Math.min(1, (now - start) / duration);
          const eased = easeInOutCubic(progress);
          const frame = tween.map((item) => ({
            name: item.name,
            color: item.color,
            pct: item.from + (item.to - item.from) * eased,
          }));

          this.paint(frame);

          if (progress < 1) {
            this.raf = requestAnimationFrame(tick);
            return;
          }

          this.paint(next);
          previousByChart.set('metal', next);
        };

        this.raf = requestAnimationFrame(tick);
      }
    }
  );
}
